import { useCallback, useEffect, useRef, useState } from 'react';
import FlashlightOffIcon from '@mui/icons-material/FlashlightOff';
import FlashlightOnIcon from '@mui/icons-material/FlashlightOn';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  CircularProgress,
  Box,
  IconButton,
} from '@mui/material';

import { BarcodeReader } from '../../../../../common/helpers';
import { LoadingOverlay } from '../../../../../common';

const RESUME_SCANNING_DELAY_MS = 150;
const DEFAULT_IOS_ZOOM = 5;

export default function BarcodeScanner({ onDetected, processing = false }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const videoTrackRef = useRef(null);
  const scanInterval = useRef(null);
  const lastResultRef = useRef(null);

  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [barcodeReader, setBarcodeReader] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [zoomSupported, setZoomSupported] = useState(false);
  const [zoomRange, setZoomRange] = useState({ min: 1, max: 3, step: 0.1 });
  const [isFlashOn, setIsFlashOn] = useState(false);
  const [isFlashAvailable, setIsFlashAvailable] = useState(false);

  const iosDefaultZoom = DEFAULT_IOS_ZOOM;

  const stopCurrentStream = () => {
    if (scanInterval.current) {
      clearInterval(scanInterval.current);
      scanInterval.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => {
        try {
          t.stop();
        } catch (e) {
          console.error('Failed to stop track:', e);
        }
      });
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  useEffect(() => {
    async function fetchCameras() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        stream.getTracks().forEach((track) => track.stop());

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = devices.filter((d) => d.kind === 'videoinput');

        setCameras(videoInputs);

        const backCam =
          videoInputs.find((cam) => /back|rear|environment/i.test(cam.label)) ||
          videoInputs.find((cam) => /main|primary/i.test(cam.label)) ||
          videoInputs[0];

        if (backCam) setSelectedCameraId(backCam.deviceId);
        else setError('Não foi encontrada uma câmera adequada');
      } catch (e) {
        setError(
          'Erro ao obter as câmeras: ' + (e.message || 'Erro desconhecido')
        );
      }
    }
    fetchCameras();
  }, []);

  useEffect(() => {
    async function initBarcodeReader() {
      try {
        const { barcodeReaderError } = await BarcodeReader.setup();
        if (barcodeReaderError) {
          setError(
            'BarcodeReader não disponível: ' + barcodeReaderError.message
          );
          return;
        }
        const reader = await BarcodeReader.create([]);
        setBarcodeReader(reader);
      } catch (e) {
        setError(
          'Erro ao inicializar BarcodeReader: ' +
            (e?.message || 'Erro desconhecido')
        );
      }
    }
    initBarcodeReader();
  }, []);

  useEffect(() => {
    if (!selectedCameraId || !videoRef.current) return;
    let cancelled = false;

    const startStream = async () => {
      stopCurrentStream();
      setScanning(false);
      setError(null);
      lastResultRef.current = null;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: selectedCameraId } },
          audio: false,
        });

        if (cancelled || !videoRef.current) return;

        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        const tracks = stream.getVideoTracks();
        if (tracks.length === 0) {
          setError('Não foi encontrada uma câmera adequada');
          return;
        }
        const track = tracks[0];
        videoTrackRef.current = track;

        try {
          const capabilities = track.getCapabilities?.();

          const torchCapabilities = capabilities?.torch?.available;
          if (torchCapabilities) {
            setIsFlashAvailable(true);
          }

          const zoomCapabilities = capabilities?.zoom;
          if (zoomCapabilities) {
            const {
              min = 1,
              max = 3,
              step = 0.5,
              default: def = 1,
            } = zoomCapabilities;
            setZoomSupported(true);
            setZoomRange({ min, max, step });
            const isIPhone = /iPhone/.test(navigator.userAgent);

            const initialZoom = isIPhone
              ? Math.min(max, Math.max(min, iosDefaultZoom))
              : Math.min(max, Math.max(min, def));

            setZoom(initialZoom);
            try {
              await track.applyConstraints({
                advanced: [{ zoom: initialZoom }],
              });
            } catch {
              try {
                await track.applyConstraints({ zoom: initialZoom });
              } catch {} // eslint-disable-line no-empty
            }
          } else {
            setZoomSupported(false);
            setZoomRange({ min: 1, max: 3, step: 0.1 });
            setZoom(1);
          }
        } catch {
          setZoomSupported(false);
          setZoomRange({ min: 1, max: 3, step: 0.1 });
          setZoom(1);
        }

        await videoRef.current.play();
        setScanning(true);
      } catch (e) {
        setError(
          'Erro ao acessar a câmera: ' + (e?.message || 'Erro desconhecido')
        );
      }
    };

    startStream();

    return () => {
      cancelled = true;
      stopCurrentStream();
    };
  }, [selectedCameraId, iosDefaultZoom]);

  const handleCodeDetection = useCallback(async () => {
    if (!videoRef.current || !barcodeReader || processing) return;

    try {
      const result = await barcodeReader.detect(videoRef.current);
      const code = result?.rawValue;
      if (code && code !== lastResultRef.current) {
        lastResultRef.current = code;
        if (scanInterval.current) {
          clearInterval(scanInterval.current);
          scanInterval.current = null;
        }
        setScanning(false);
        if (videoRef.current && !videoRef.current.paused) {
          videoRef.current.pause();
        }
        if (onDetected && typeof onDetected === 'function') {
          // eslint-disable-next-line no-console
          console.log('Código detectado:', code);
          onDetected(code);
        } else {
          // eslint-disable-next-line no-console
          console.warn('onDetected no es una función válida');
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Barcode detection error:', error);
    }
  }, [barcodeReader, onDetected, processing]);

  const toggleFlash = async () => {
    if (!videoTrackRef.current || !isFlashAvailable) return;

    try {
      const newFlashState = !isFlashOn;
      await videoTrackRef.current.applyConstraints({
        advanced: [{ torch: newFlashState }],
      });
      setIsFlashOn(newFlashState);
    } catch (error) {
      try {
        await videoTrackRef.current.applyConstraints({
          torch: !isFlashOn,
        });
        setIsFlashOn(!isFlashOn);
      } catch (fallbackError) {
        // eslint-disable-next-line no-console
        console.warn('Flash not supported:', fallbackError);
      }
    }
  };

  useEffect(() => {
    if (!scanning || !barcodeReader || !videoRef.current) return;

    scanInterval.current = window.setInterval(handleCodeDetection, 800);

    return () => {
      if (scanInterval.current) {
        clearInterval(scanInterval.current);
        scanInterval.current = null;
      }
    };
  }, [scanning, barcodeReader, handleCodeDetection]);

  useEffect(() => {
    if (processing) {
      if (scanInterval.current) {
        clearInterval(scanInterval.current);
        scanInterval.current = null;
      }
      setScanning(false);
      videoRef.current?.pause();
      return;
    }

    const timer = setTimeout(async () => {
      if (videoRef.current && !processing) {
        lastResultRef.current = null;
        if (videoRef.current.paused) {
          try {
            await videoRef.current.play();
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Erro ao reproduzir vídeo:', error);
          }
        }
        setScanning(true);
      }
    }, RESUME_SCANNING_DELAY_MS);

    return () => clearTimeout(timer);
  }, [processing]);

  useEffect(() => {
    if (!zoomSupported || !videoTrackRef.current) return;
    const track = videoTrackRef.current;
    (async () => {
      try {
        await track.applyConstraints({ advanced: [{ zoom }] });
      } catch {
        try {
          await track.applyConstraints({ zoom });
        } catch {}
      }
    })();
  }, [zoom, zoomSupported]);

  useEffect(() => {
    return () => {
      stopCurrentStream();
    };
  }, []);

  return (
    <div>
      {error && <p style={{ color: 'red' }}>{error}</p>}

      <Box sx={{ display: 'flex', alignItems: 'end' }}>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel id="select-camera">Selecionar câmera</InputLabel>
          <Select
            labelId="select-camera"
            id="select-camera"
            value={selectedCameraId || ''}
            label="Selecionar câmera"
            size="small"
            onChange={(e) => setSelectedCameraId(e.target.value)}
          >
            {cameras.map((cam) => (
              <MenuItem key={cam.deviceId} value={cam.deviceId}>
                {cam.label || `Câmera ${cam.deviceId}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <div style={{ marginTop: 10 }}>
        <Stack spacing={2} direction="row" sx={{ alignItems: 'center' }}>
          <ZoomOutIcon
            sx={{ cursor: 'pointer' }}
            color="primary"
            onClick={() =>
              setZoom((z) =>
                Math.max(
                  zoomRange.min,
                  Number((z - (zoomRange.step || 0.5)).toFixed(2))
                )
              )
            }
          />
          <Slider
            aria-label="Zoom"
            min={zoomRange.min}
            max={zoomRange.max}
            step={zoomRange.step}
            value={zoom}
            valueLabelDisplay="auto"
            onChange={(_, value) => setZoom(value)}
          />
          <ZoomInIcon
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={() =>
              setZoom((z) =>
                Math.min(
                  zoomRange.max,
                  Number((z + (zoomRange.step || 0.5)).toFixed(2))
                )
              )
            }
          />
          <IconButton
            color="primary"
            size="small"
            onClick={toggleFlash}
            disabled={!isFlashAvailable}
            title={
              isFlashAvailable ? 'Luz de flash' : 'Luz de flash não disponível'
            }
          >
            {isFlashOn ? <FlashlightOnIcon /> : <FlashlightOffIcon />}
          </IconButton>
        </Stack>
      </div>

      <div
        style={{
          width: '100%',
          marginTop: 10,
          borderRadius: 8,
          overflow: 'hidden',
          objectFit: 'cover',
          position: 'relative',
        }}
      >
        <video
          ref={videoRef}
          style={{
            width: '100%',
            display: 'block',
            transform: zoomSupported ? 'none' : `scale(${zoom})`,
            transformOrigin: 'center',
          }}
          muted
          autoPlay
          playsInline
        />
        {processing && (
          <LoadingOverlay sx={{ position: 'absolute', zIndex: 1 }}>
            <CircularProgress color="inherit" />
          </LoadingOverlay>
        )}
      </div>
    </div>
  );
}
